import {AfterViewInit, ChangeDetectionStrategy, Component, Inject, signal, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {combineLatest, Subject} from 'rxjs';
import {debounceTime, first, startWith, switchMap, tap} from 'rxjs/operators';
import {DiscountSyncLog, DownloadRequestBody, DownloadRequestContent, DownloadRequestContentColumn, MerchantDiscount, PagedRequest} from '../../../core/dtos/api';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {DashboardApiService} from '../../../core/services/api/dashboard-api.service';
import {MatTableDataSource} from '@angular/material/table';
import {FormBuilder} from '@angular/forms';
import {DiscountApiService} from '../../../core/services/api/discount-api.service';
import {environment} from '../../../../environments/environment';
import {DashboardHttpService} from '../../../core-services/dashboard-http-service/dashboard-http.service';
import {SnackBarService} from '../../../core-services/snack-bar/snack-bar.service';

@Component({
    templateUrl: './shopify-audit-log-dialog.component.html',
    styleUrls: ['./shopify-audit-log-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ShopifyAuditLogDialogComponent implements AfterViewInit {
  constructor(@Inject(MAT_DIALOG_DATA) public data: {merchantDiscount: MerchantDiscount},
              private discountsApiService: DiscountApiService, private dashboardApiService: DashboardApiService,
              private fb: FormBuilder, private dashboardHttpService: DashboardHttpService, private snackBarService: SnackBarService) {}

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  ngAfterViewInit$ = new Subject<void>();
  statusSelectControl = this.fb.control<'all' | 'synced' | 'failed'>('all');
  requestParams = {} as PagedRequest;
  dataSource = new MatTableDataSource<DiscountSyncLog>();
  totalItems = signal(0);
  isLoadingResults = signal(true);

  readonly displayColumns = ['discount_source_id', 'discount_sync_log_result', 'discount_name', 'coupon_code', 'discount_sync_log_message'];

  dataSource$Sub = this.ngAfterViewInit$.pipe(
    switchMap(() => combineLatest([
      this.dashboardApiService.currentAccount$,
      this.paginator.page.pipe(debounceTime(250), startWith({})),
      this.sort.sortChange.pipe(debounceTime(250), startWith({}), tap(() => this.paginator.pageIndex = 0)),
      this.statusSelectControl.valueChanges.pipe(startWith('all'), tap(() => this.paginator.pageIndex = 0))
      ])
    ),
    tap(() => this.isLoadingResults.set(true)),
    switchMap(([currentAccount, page, sort, status]) => {
      let pagedRequest = {
        sort: this.sort.active,
        direction: this.sort.direction.toUpperCase(),
        page: this.paginator.pageIndex + 1,
        limit: this.paginator.pageSize
      } as PagedRequest;
      if (this.statusSelectControl.value !== 'all') {
        pagedRequest.discount_sync_log_result = this.statusSelectControl.value;
      }
      this.requestParams = pagedRequest;
      return this.discountsApiService.getPagedMerchantDiscountSyncLogs(currentAccount.entity_id, pagedRequest);
    })
  ).subscribe({
    next: (data) => {
      this.dataSource.data = data.items.map(item => ({...item, discount_source_id: item.discount_source_id.match(/\d+$/)[0]}));
      this.paginator.length = data.total_items;
      this.isLoadingResults.set(false);
    },
    error: (error) => {
      this.snackBarService.openErrorSnackBar('There was an error when loading audit log items.');
    }
  });

  onDownloadClick() {
    this.dashboardApiService.currentAccount$.pipe(switchMap(currentAccount => {
        const columns: DownloadRequestContentColumn[] = [
          {column: 'discount_source_id', header: 'Shopify Discount ID'},
          {column: 'discount_sync_log_result', header: 'Status'},
          {column: 'discount_name', header: 'Discount Title'},
          {column: 'coupon_code', header: 'Coupon Code'},
          {column: 'discount_sync_log_message', header: 'File Message'},
        ];
        let params = {
          ...this.requestParams,
          sort: this.requestParams.sort && this.requestParams.direction
            ? 'dsl.' + this.requestParams.sort : 'dsl.sort_updated_at',
          direction: this.requestParams.direction ? this.requestParams.direction.toUpperCase() : 'DESC',
        };
        // Remove the page and limit property from params.
        let {page, limit, ...updateParams} = params;

        let paramsForURL = new URLSearchParams(Object.entries(updateParams) as string[][]).toString();
        let requestContent: DownloadRequestContent = {
          request_url: `/merchants/${currentAccount.entity_id}/discounts/logs?${paramsForURL}`,
          columns,
          format: 'csv'
        };
        let downloadRequestBody: DownloadRequestBody = {
          request: JSON.stringify(requestContent)
        };
        return this.dashboardHttpService.downloadPostBlobWithOptions(`${environment.apiUrl}/download`, downloadRequestBody, 'shopify-sync-results');
      }),
      first()
    ).subscribe({
        error: (error) => {
          this.snackBarService.openErrorSnackBar('There was an error when downloading audit log items.');
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.ngAfterViewInit$.next();
    this.ngAfterViewInit$.complete();
  }
}
